import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {debounceTime, switchMap} from 'rxjs/operators';
import {BaseExecuteViewService} from './base-execute-view.service';
import {ExecutorModel} from '../models/executor.model';
import {ImmutableListRepositoryService} from '../../../lib/gelios-field/immutable-list-repository.service';
import {BaseUsersCardComponent} from '../../shared/card-view/users/base-users-card.component';
import {executorStatusClr} from '../models/executor-status.model';
import {EmployeeExtendedRegistrationTypes} from '../../utility/extended-registration-status-type';
import {RouteLibraryEntity} from '../../route/route-library-entity';
import {ExecutorViewType, ExecutorViewTypes} from './executor-view-tabs.model';
import {UnitCardTabTypes} from '../../unit-card/unit-card.model';
import {UnitModel} from '../../unit/unit.model';
import {ExecutorOwnedUnitListService} from './executor-owned-unit-list.service';
import {ExecutorCoOwnedUnitListService} from './executor-co-owned-unit-list.service';
import {RevokeUnitFromExecutorService} from './revoke-unit-from-executor.service';
import {NotificationsService} from 'angular2-notifications';
import {TabVisibilityManager} from '../../shared/card-view/card-view-tab/tab-visibility-manager.component';

@Component({
  selector: 'app-courier-execute-view',
  templateUrl: './base-execute-view.component.html',
  styleUrls: ['../../shared/card-view/users/base-users-card.component.scss']
})
export class BaseExecuteViewComponent extends BaseUsersCardComponent implements OnInit, OnDestroy {
  protected readonly ExecutorViewTypes = ExecutorViewTypes;
  routeEntity: RouteLibraryEntity;
  model: BehaviorSubject<ExecutorModel> = new BehaviorSubject(null);
  ownedUnits: BehaviorSubject<UnitModel[]> = new BehaviorSubject<UnitModel[]>([]);
  coOwnedUnits: BehaviorSubject<UnitModel[]> = new BehaviorSubject<UnitModel[]>([]);
  registrationFormIsAllowed = false;
  registrationFormIsOpen = false;
  fullName = '';
  updateExecutorSubject: Subject<boolean> = new Subject<boolean>();
  tabManager = new TabVisibilityManager<ExecutorViewType>({
    [ExecutorViewTypes.UnitsOwned]: false,
    [ExecutorViewTypes.UnitsCoOwned]: false,
    [ExecutorViewTypes.Employees]: false,
  });

  protected readonly UnitCardTabTypes = UnitCardTabTypes;

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected service: BaseExecuteViewService,
    protected immutableList: ImmutableListRepositoryService,
    protected executorOwnedUnitListService: ExecutorOwnedUnitListService,
    protected executorCoOwnedUnitListService: ExecutorCoOwnedUnitListService,
    protected revokeUnitFromExecutorService: RevokeUnitFromExecutorService,
    protected notificationsService: NotificationsService,
  ) {
    super();
    this.subscriptions.push(
      this.router.events.subscribe(
        e => {
          this.model.next(null);
          this.ownedUnits.next([]);
          this.coOwnedUnits.next([]);
          this.tabManager.resetTabVisibility();
          if (e instanceof NavigationEnd) {
            this.updateExecutor();
          }
        }
      )
    );

    this.subscriptions.push(
      this.updateExecutorSubject.pipe(
        debounceTime(250),
        switchMap(
          () => this.service.getExecute(this.activatedRoute.snapshot.params.id)
        )
      ).subscribe(
        response => {
          if (response) {
            this.model.next(response);
            this.reloadOwnedUnitList(response.id);
            this.reloadCoOwnedUnitList(response.id);
            this.fullName = this.getFullName(response);
            this.profileStatusAvatarModel = {
              avatar: response.executor_photo,
              status_class: executorStatusClr[response.status],
              status_extended_registration_class: ''
            };
          }
        }
      )
    );
  }

  reloadOwnedUnitList(executorId) {
    this.executorOwnedUnitListService.getUnits(executorId).subscribe(
      u => this.ownedUnits.next(u)
    );
  }
  reloadCoOwnedUnitList(executorId) {
    this.executorCoOwnedUnitListService.getUnits(executorId).subscribe(
      u => this.coOwnedUnits.next(u)
    );
  }

  updateExecutor() {
    this.updateExecutorSubject.next(true);
  }

  revokeUnit(unit: UnitModel) {
    this.revokeUnitFromExecutorService.revoke(unit.id, this.model.getValue().id).subscribe(
      response => {
        this.updateExecutor();
      },
      error => {
        this.notificationsService.error('Server error');
        this.updateExecutor();
      }
    );
  }

  toggleRegistrationForm() {
    this.registrationFormIsOpen = !this.registrationFormIsOpen;
  }

  ngOnInit() {
    this.updateExecutor();
  }

  isExtendedRegistrationStatus(status: string) {
    return status && status !== EmployeeExtendedRegistrationTypes.ABSENT;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.tabManager.resetTabVisibility();
  }
}
